import { formatPhoneNumber } from '@dispatch/shared';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { PaginatedQueryResult } from '@dispatch/shared/hooks';
import {
  FNOL_RequestForm,
  FNOL_TaskTypes,
  QualifiedResourcePost,
  QualifiedResourcesFNOLFilters,
  ResultStatus,
} from '@dispatch/shared/types';
import {
  htmlColumn,
  Loader,
  TableComponent,
  TableProps,
  textColumn,
} from '@dispatch/web/ui';
import { Button, Stack, Tooltip } from '@mui/material';
import { GridCheckIcon, GridSortModel } from '@mui/x-data-grid';
import { isEmpty, isNull } from 'lodash';
import React, { useMemo } from 'react';
import { UseFormGetValues } from 'react-hook-form';
import { useIsFetching } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { DataGridProProps } from '@mui/x-data-grid-pro';

export type QualifiedResourcesListSectionProps = {
  taskType: FNOL_TaskTypes;
  status: ResultStatus;
  selectedResource: QualifiedResourcePost | null;
  setSelectedResource: React.Dispatch<
    React.SetStateAction<QualifiedResourcePost | null>
  >;
  qualifiedResources?: QualifiedResourcePost[];
  timeZone?: string;
  getValues: UseFormGetValues<FNOL_RequestForm>;
  qualifiedResourcesFilters: QualifiedResourcesFNOLFilters | null;
  setQualifiedResourcesFilters: React.Dispatch<
    React.SetStateAction<QualifiedResourcesFNOLFilters>
  >;
  qualifiedResourcesPaging?: PaginatedQueryResult<
    QualifiedResourcePost[],
    unknown
  >;
};

export const QualifiedResourcesListSection = ({
  taskType,
  status,
  setSelectedResource,
  qualifiedResources = [],
  timeZone = '',
  getValues,
  selectedResource,
  qualifiedResourcesFilters,
  qualifiedResourcesPaging,
  setQualifiedResourcesFilters,
}: QualifiedResourcesListSectionProps) => {
  const navigate = useNavigate();
  const columns = useMemo(() => {
    const columns = [
      htmlColumn({
        field: 'name',
        headerName: 'Name',
        minWidth: 150,
        sortable: false,
        renderCell: (params: any) => {
          return (
            <Button
              type="button"
              variant="text"
              onClick={() =>
                navigate(
                  `/tasks/add-qualified/${params.row.id}?tab=0&readonly=true`
                )
              }
            >
              <strong>{params.row.name}</strong>
            </Button>
          );
        },
      }),
      htmlColumn({
        field: 'locations',
        headerName: 'Address',
        sortable: false,
        minWidth: 150,
        renderCell: (params: any) => {
          const { streetLine1, streetLine2, city, state, zip } =
            params.row.locations || {};
          const addressLines = [
            streetLine1,
            streetLine2,
            [city, state, zip].filter(Boolean).join(', '),
          ].filter((line) => !isEmpty(line));
          const tooltipText = addressLines.join('\n');
          return (
            <Tooltip title={tooltipText}>
              <span>{tooltipText}</span>
            </Tooltip>
          );
        },
      }),
      textColumn({
        field: 'phone',
        headerName: 'Phone',
        flex: 1,
        sortable: false,
        valueFormatter: (params: any) => {
          const phone = !isEmpty(params) ? params : '--';
          return formatPhoneNumber(phone);
        },
      }),
      textColumn({
        field: 'email',
        headerName: 'Email',
        flex: 1,
        sortable: false,
      }),
      htmlColumn({
        field: 'attributes',
        headerName: 'Attributes',
        flex: 1,
        minWidth: 250,
        sortable: false,
        renderCell: ({ value }) => {
          const attributes = value?.flatMap((attr: any) =>
            attr.selectedAttributeValuesVarchars?.flatMap(
              (val: any) => val.displayName
            )
          );

          const displayedAttributes = attributes?.length
            ? attributes.join(', ')
            : '--';

          return (
            <Tooltip title={`${displayedAttributes}`}>
              <span>{`${displayedAttributes}`}</span>
            </Tooltip>
          );
        },
      }),
      textColumn({
        field: 'distanceToTask',
        headerName: 'Distance',
        flex: 1,
        valueFormatter: (params: any) => {
          const distance = params?.value ?? '--';
          return `${distance} miles`;
        },
      }),
      htmlColumn({
        field: 'selectResource',
        headerName: ' ',
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          if (
            !isNull(selectedResource) &&
            selectedResource.id === params.row.id
          ) {
            return <GridCheckIcon />;
          }
          return (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                setSelectedResource(params.row);
              }}
            >
              Select
            </Button>
          );
        },
      }),
    ];

    return columns;
  }, [getValues, taskType, timeZone, selectedResource]);

  const getPaginationConfig = (pageSize: number) => ({
    pageSize,
    pagingOptions: [5, 10, 15, 20],
  });

  const handleSort = (sort: GridSortModel) => {
    setQualifiedResourcesFilters((prev) => {
      return { ...prev, distanceToTaskSort: sort?.[0]?.sort };
    });
  };

  const isLoading = useIsFetching(['resources-as-post']);
  const tableArgs: TableProps<QualifiedResourcePost> = React.useMemo(() => {
    return {
      dataGridProps: {
        disableSelectionOnClick: true,
      },
      rows: qualifiedResources || [],
      columns,
      height: 600,
      emptyEntityName: 'QualifiedResources',
      status: isLoading ? 'loading' : status,
      getRowId: (row) => row.id,
      handleSort,
    };
  }, [selectedResource, qualifiedResources, isLoading]);

  const NoRowsOverlay = () => (
    <Stack direction="row" justifyContent={'center'} alignItems="center" mt={2}>
      <p>No Qualified Resources</p>
    </Stack>
  );

  const isFetchingResources = useIsFetching(['resources-as-post']);
  const handleOnRowsScrollEnd = React.useCallback<
    NonNullable<DataGridProProps['onRowsScrollEnd']>
  >(async () => {
    if (qualifiedResourcesPaging?.hasNextPage && !isFetchingResources) {
      qualifiedResourcesPaging.getNextPage();
    }
  }, [qualifiedResourcesPaging, isFetchingResources]);

  return (
    <div style={{ height: '85vh', width: '100%' }}>
      <TableComponent
        {...tableArgs}
        tableHeight="85vh"
        columns={columns}
        dataGridProps={{
          onRowsScrollEnd: handleOnRowsScrollEnd,
          disableSelectionOnClick: true,
          components: {
            MoreActionsIcon: MoreHorizIcon,
            NoRowsOverlay,
            NoResultsOverlay: undefined,
            LoadingOverlay: Loader,
          },
        }}
      />
    </div>
  );
};
